import { ProcessedCertificate } from '../types';
import { CourseSession, CourseSessionAttendee, Customer, GeneratedCertificate } from '../types/crm';
import { courseSessionsService } from '../services/courseSessionsService';
import { supabaseCustomerStorage, supabaseInstructorStorage } from '../services/supabaseStorage';
import { upsertIssuedCertificateFromGenerated } from '../services/issuedCertificatesService';
import { calculateExpiryDate, generateCertificatePDF } from './pdfGenerator';
import { formatDateUkForFileName } from './paymentFormUtils';
import { assertAttendeeCertificateEligible } from './sessionAttendeeStatus';
import {
  findExistingCertificateNumber,
  findMatchingGeneratedCertificateIndex,
  isAllocatedCertificateNumber,
  parseAttendeeIdFromCertificateId,
  parseSessionIdFromCertId,
} from './certificateNumber';
import { buildSessionReferenceIndex, sessionReferenceCode } from '../components/dashboard/trainingCalendarUtils';

export type GeneratedCertificateSource = GeneratedCertificate & {
  customerId: string;
  customerName: string;
  sessionId?: string;
};

export function parseAttendeeIdFromCertId(certId: string): string | undefined {
  return parseAttendeeIdFromCertificateId(certId);
}

function firstValidEmail(...candidates: Array<string | null | undefined>): string {
  for (const raw of candidates) {
    const email = String(raw || '').trim();
    if (email.includes('@')) return email;
  }
  return '';
}

function emailsFromCustomer(customer: Customer | null | undefined): string[] {
  if (!customer) return [];
  const out: string[] = [];
  const primary = firstValidEmail(customer.emailAddress);
  if (primary) out.push(primary);
  for (const contact of customer.contacts || []) {
    const email = firstValidEmail(contact.email);
    if (email && !out.some((e) => e.toLowerCase() === email.toLowerCase())) {
      out.push(email);
    }
  }
  return out;
}

function contactEmailMatchingName(customer: Customer | null | undefined, attendeeName: string): string {
  const needle = String(attendeeName || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, ' ');
  if (!customer?.contacts?.length || !needle) return '';
  const parts = needle.split(' ').filter(Boolean);
  for (const contact of customer.contacts) {
    const full = `${contact.firstName || ''} ${contact.lastName || ''}`.trim().toLowerCase().replace(/\s+/g, ' ');
    if (!full) continue;
    if (full === needle) return firstValidEmail(contact.email);
    if (parts.length >= 2 && parts.every((p) => full.includes(p))) {
      return firstValidEmail(contact.email);
    }
  }
  return '';
}

export type CertificateSendRecipient = {
  email: string;
  greetingName: string;
  source: 'individual' | 'individual_contact' | 'company_contact' | 'company' | 'none';
};

/** Resolve the best email to send a certificate to for an attendee CRM / session seat. */
export async function resolveCertificateSendRecipient(
  row: GeneratedCertificateSource
): Promise<CertificateSendRecipient> {
  const attendeeName = displayGeneratedAttendeeName(row, row.customerName);
  const greetingName =
    attendeeName.split(/\s+/).filter(Boolean)[0] ||
    row.customerName.split(/\s+/).filter(Boolean)[0] ||
    'there';

  const sessionId = row.sessionId || parseSessionIdFromCertId(row.id);
  const attendeeId = row.attendeeId || parseAttendeeIdFromCertId(row.id);

  let attendee: CourseSessionAttendee | undefined;
  if (sessionId) {
    try {
      const bySession = await courseSessionsService.listAttendeesBySessionIds([sessionId]);
      const roster = bySession[sessionId] || [];
      if (attendeeId) {
        attendee = roster.find((a) => a.id === attendeeId);
      }
      if (!attendee && attendeeName) {
        const needle = attendeeName.trim().toLowerCase();
        attendee = roster.find((a) => {
          const enrollment = `${a.enrollmentFirstName || ''} ${a.enrollmentSurname || ''}`.trim().toLowerCase();
          return enrollment && enrollment === needle;
        });
      }
      // Prefer the individual CRM linked on this cert row.
      if (!attendee && row.customerId) {
        attendee = roster.find((a) => a.individualCustomerId === row.customerId);
      }
    } catch {
      /* ignore */
    }
  }

  const individualId = attendee?.individualCustomerId || row.customerId;
  const companyId =
    attendee?.sponsoringCompanyCustomerId ||
    attendee?.companyCustomerId ||
    attendee?.payerCustomerId ||
    null;

  const [individual, company] = await Promise.all([
    individualId ? supabaseCustomerStorage.getCustomerById(individualId) : Promise.resolve(undefined),
    companyId && companyId !== individualId
      ? supabaseCustomerStorage.getCustomerById(companyId)
      : Promise.resolve(undefined),
  ]);

  const individualPrimary = firstValidEmail(individual?.emailAddress);
  if (individualPrimary) {
    return { email: individualPrimary, greetingName, source: 'individual' };
  }

  const individualContact = firstValidEmail(...emailsFromCustomer(individual).slice(1));
  if (individualContact) {
    return { email: individualContact, greetingName, source: 'individual_contact' };
  }

  const namedCompanyContact = contactEmailMatchingName(company, attendeeName);
  if (namedCompanyContact) {
    return { email: namedCompanyContact, greetingName, source: 'company_contact' };
  }

  const companyPrimary = firstValidEmail(company?.emailAddress, ...(company?.contacts || []).map((c) => c.email));
  if (companyPrimary) {
    return { email: companyPrimary, greetingName, source: 'company' };
  }

  // Last resort: the customer the cert is stored on (already tried as individual, but covers edge cases).
  if (!individual || individual.id !== row.customerId) {
    const storedOn = await supabaseCustomerStorage.getCustomerById(row.customerId);
    const fallback = firstValidEmail(storedOn?.emailAddress, ...(storedOn?.contacts || []).map((c) => c.email));
    if (fallback) {
      return {
        email: fallback,
        greetingName,
        source: storedOn?.customerType === 'company' ? 'company' : 'individual',
      };
    }
  }

  return { email: '', greetingName, source: 'none' };
}

export function displayGeneratedAttendeeName(cert: GeneratedCertificate, customerName: string): string {
  const attendee = String(cert.attendeeName || '').trim();
  const customer = String(customerName || '').trim();
  if (attendee && attendee.toLowerCase() !== 'unknown attendee') return attendee;
  if (customer) return customer;
  return attendee || 'Unknown attendee';
}

function sanitizeFilePart(raw: string): string {
  return raw
    .replace(/[<>:"/\\|?*]+/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 80) || 'certificate';
}

export function buildDefaultCertificateFileName(
  courseName: string,
  attendeeName: string,
  courseDateIso: string
): string {
  return `${sanitizeFilePart(courseName)}-${sanitizeFilePart(attendeeName)}-${formatDateUkForFileName(courseDateIso)}.pdf`;
}

function resolveCertificateFileName(args: {
  cert: GeneratedCertificate;
  attendeeName: string;
  courseDate: string;
}): string {
  const { cert, attendeeName, courseDate } = args;
  const defaultName = buildDefaultCertificateFileName(cert.courseName, attendeeName, courseDate);
  const custom = String(cert.fileName || '').trim();
  if (!custom) return defaultName;

  const legacyWithRawIso = `${sanitizeFilePart(cert.courseName)}-${sanitizeFilePart(attendeeName)}-${courseDate}.pdf`;
  if (custom === legacyWithRawIso || custom === defaultName) return defaultName;

  const isoDateSuffix = custom.match(/-(\d{4}-\d{2}-\d{2})\.pdf$/i);
  if (isoDateSuffix) {
    return custom.replace(/(\d{4}-\d{2}-\d{2})(\.pdf)$/i, `${formatDateUkForFileName(courseDate)}$2`);
  }

  return custom;
}

export type GeneratedCertificateEditDraft = {
  attendeeName: string;
  courseName: string;
  courseDate: string;
  instructorName: string;
  instructorRegNumber: string;
  truckType: string;
  truckMake: string;
  truckCapacity: string;
  truckAttachments: string;
  status: GeneratedCertificate['status'];
  fileName: string;
};

export function buildCertificateEditDraft(row: GeneratedCertificateSource): GeneratedCertificateEditDraft {
  const attendeeName = displayGeneratedAttendeeName(row, row.customerName);
  return {
    attendeeName,
    courseName: row.courseName || '',
    courseDate: row.courseDate || '',
    instructorName: row.instructorName || '',
    instructorRegNumber: row.instructorRegNumber || '',
    truckType: row.truckType || '',
    truckMake: row.truckMake || '',
    truckCapacity: row.truckCapacity || '',
    truckAttachments: row.truckAttachments || '',
    status: row.status || 'active',
    fileName: resolveCertificateFileName({
      cert: row,
      attendeeName,
      courseDate: row.courseDate || ''
    })
  };
}

export async function updateGeneratedCertificate(
  row: GeneratedCertificateSource,
  draft: GeneratedCertificateEditDraft
): Promise<GeneratedCertificateSource> {
  const customer = await supabaseCustomerStorage.getCustomerById(row.customerId);
  if (!customer) throw new Error('Customer record not found.');

  const attendeeName = draft.attendeeName.trim();
  const courseName = draft.courseName.trim();
  const courseDate = draft.courseDate.trim();
  if (!attendeeName || !courseName || !courseDate) {
    throw new Error('Attendee name, course name, and course date are required.');
  }

  const certs = [...(customer.generatedCertificates || [])];
  const idx = certs.findIndex((cert) => cert.id === row.id);
  if (idx < 0) throw new Error('Certificate record not found on customer profile.');

  const expiryDate = calculateExpiryDate(courseDate, courseName) || certs[idx].expiryDate || '';
  const fileName =
    draft.fileName.trim() ||
    buildDefaultCertificateFileName(courseName, attendeeName, courseDate);

  const updated: GeneratedCertificate = {
    ...certs[idx],
    attendeeName,
    courseName,
    courseDate,
    expiryDate,
    fileName,
    status: draft.status,
    instructorName: draft.instructorName.trim() || undefined,
    instructorRegNumber: draft.instructorRegNumber.trim() || undefined,
    truckType: draft.truckType.trim() || undefined,
    truckMake: draft.truckMake.trim() || undefined,
    truckCapacity: draft.truckCapacity.trim() || undefined,
    truckAttachments: draft.truckAttachments.trim() || undefined
  };

  certs[idx] = updated;
  await supabaseCustomerStorage.updateCustomer({
    ...customer,
    generatedCertificates: certs
  });

  return {
    ...updated,
    customerId: row.customerId,
    customerName: row.customerName,
    sessionId: row.sessionId
  };
}

function attendeeLabelFromRow(
  attendee: CourseSessionAttendee,
  customerById: Map<string, Customer>
): string {
  if (attendee.isVirtual) return attendee.placeholderLabel?.trim() || 'Reserved spot';
  const enrollmentName = `${attendee.enrollmentFirstName || ''} ${attendee.enrollmentSurname || ''}`.trim();
  if (enrollmentName) return enrollmentName;
  const customerId = attendee.individualCustomerId || attendee.companyCustomerId;
  const customer = customerId ? customerById.get(customerId) : undefined;
  if (!customer) return 'Unknown attendee';
  if (customer.customerType === 'individual') {
    return `${customer.firstName || ''} ${customer.surname || ''}`.trim() || customer.companyName || 'Unknown';
  }
  return customer.companyName || 'Unknown';
}

function buildProcessedCertificate(args: {
  cert: GeneratedCertificate;
  attendeeName: string;
  customerName: string;
  session?: CourseSession | null;
  attendeeId?: string;
}): ProcessedCertificate {
  const { cert, attendeeName, customerName, session, attendeeId } = args;
  const courseDate = cert.courseDate;
  const fileName = resolveCertificateFileName({ cert, attendeeName, courseDate });
  return {
    id: cert.id,
    attendeeName,
    companyName: customerName || attendeeName,
    courseName: cert.courseName,
    courseDate,
    courseDates: cert.courseDates?.length ? cert.courseDates : undefined,
    instructorName: cert.instructorName,
    truckType: cert.truckType?.trim() || session?.truckType?.trim() || undefined,
    truckMake: cert.truckMake?.trim() || session?.truckMake?.trim() || undefined,
    truckCapacity: cert.truckCapacity?.trim() || session?.truckCapacity?.trim() || undefined,
    truckAttachments: cert.truckAttachments?.trim() || session?.truckAttachments?.trim() || undefined,
    instructorRegNumber: cert.instructorRegNumber,
    expiryDate: cert.expiryDate || undefined,
    fileName,
    bookingId: session?.id || cert.sessionId || parseSessionIdFromCertId(cert.id),
    attendeeId: attendeeId || cert.attendeeId || parseAttendeeIdFromCertificateId(cert.id),
  };
}

export type RegenerationContext = {
  sessionById: Map<string, CourseSession>;
  attendeesBySessionId: Record<string, CourseSessionAttendee[]>;
  customerById: Map<string, Customer>;
  instructorLabelById: Map<string, string>;
  /** Human TT##### session refs keyed by session UUID — never expose UUID as the ref. */
  sessionRefById: Map<string, string>;
};

export async function createRegenerationContext(
  rows: GeneratedCertificateSource[]
): Promise<RegenerationContext> {
  const sessionIds = [
    ...new Set(
      rows
        .map((row) => row.sessionId || parseSessionIdFromCertId(row.id))
        .filter((id): id is string => !!id)
    )
  ];

  const [sessions, attendeesBySessionId, instructors] = await Promise.all([
    courseSessionsService.listSessions(),
    sessionIds.length
      ? courseSessionsService.listAttendeesBySessionIds(sessionIds)
      : Promise.resolve({} as Record<string, CourseSessionAttendee[]>),
    supabaseInstructorStorage.getInstructors()
  ]);

  const sessionById = new Map<string, CourseSession>();
  sessions.forEach((session) => {
    if (sessionIds.includes(session.id)) sessionById.set(session.id, session);
  });

  const customerIds = new Set<string>();
  Object.values(attendeesBySessionId).forEach((attendees) => {
    attendees.forEach((attendee) => {
      const cid = attendee.individualCustomerId || attendee.companyCustomerId;
      if (cid) customerIds.add(cid);
    });
  });
  rows.forEach((row) => {
    if (row.customerId) customerIds.add(row.customerId);
  });

  const customerById = new Map<string, Customer>();
  await Promise.all(
    [...customerIds].map(async (id) => {
      const customer = await supabaseCustomerStorage.getCustomerById(id);
      if (customer) customerById.set(id, customer);
    })
  );

  const instructorLabelById = new Map<string, string>();
  (instructors || []).forEach((instructor) => {
    const label = `${instructor.firstName || ''} ${instructor.lastName || ''}`.trim();
    if (label) instructorLabelById.set(instructor.id, label);
  });

  const refIndex = buildSessionReferenceIndex(
    sessions.map((s) => ({
      id: s.id,
      courseName: s.courseName,
      createdAt: s.createdAt,
      dateBlocks: s.dateBlocks || [],
    }))
  );
  const sessionRefById = new Map<string, string>();
  sessionById.forEach((_session, sid) => {
    sessionRefById.set(sid, sessionReferenceCode(sid, refIndex.get(sid)));
  });

  return { sessionById, attendeesBySessionId, customerById, instructorLabelById, sessionRefById };
}

export async function resolveProcessedCertificate(
  row: GeneratedCertificateSource,
  context?: RegenerationContext
): Promise<ProcessedCertificate> {
  const sessionId = row.sessionId || parseSessionIdFromCertId(row.id);
  const attendeeId = row.attendeeId || parseAttendeeIdFromCertId(row.id);
  const ctx = context || (await createRegenerationContext([row]));

  const session = sessionId ? ctx.sessionById.get(sessionId) : undefined;
  const attendee =
    sessionId && attendeeId
      ? (ctx.attendeesBySessionId[sessionId] || []).find((a) => a.id === attendeeId)
      : undefined;

  let attendeeName = displayGeneratedAttendeeName(row, row.customerName);
  if (
    attendee &&
    (!String(row.attendeeName || '').trim() ||
      String(row.attendeeName).trim().toLowerCase() === 'unknown attendee')
  ) {
    attendeeName = attendeeLabelFromRow(attendee, ctx.customerById);
  }

  const customerRecord = ctx.customerById.get(row.customerId);
  const customerName =
    customerRecord?.customerType === 'company'
      ? customerRecord.companyName || row.customerName
      : row.customerName;

  const instructorName =
    row.instructorName ||
    (session?.instructorId ? ctx.instructorLabelById.get(session.instructorId) : undefined);

  const processed = buildProcessedCertificate({
    cert: row,
    attendeeName,
    customerName: customerName || attendeeName,
    session,
    attendeeId
  });
  if (instructorName) processed.instructorName = instructorName;

  const existingVin = await findExistingCertificateNumber(
    {
      currentId: processed.id,
      sessionId,
      attendeeId: processed.attendeeId || attendeeId,
      attendeeName: processed.attendeeName,
      courseName: processed.courseName,
      courseDate: processed.courseDate,
      customerId: row.customerId,
    },
    customerRecord?.generatedCertificates
  );
  if (existingVin) processed.id = existingVin;

  return processed;
}

export async function regenerateCertificatePdf(
  row: GeneratedCertificateSource,
  context?: RegenerationContext
): Promise<{ blob: Blob; processed: ProcessedCertificate }> {
  if (String(row.status || '').toLowerCase() === 'revoked') {
    throw new Error('Certificate not allowed: revoked (seat left main roster)');
  }
  const sessionId = row.sessionId || parseSessionIdFromCertId(row.id);
  const attendeeId = row.attendeeId || parseAttendeeIdFromCertId(row.id);
  const ctx = context || (await createRegenerationContext([row]));
  const session = sessionId ? ctx.sessionById.get(sessionId) : undefined;
  const attendee =
    sessionId && attendeeId
      ? (ctx.attendeesBySessionId[sessionId] || []).find((a) => a.id === attendeeId)
      : undefined;
  if (attendee) {
    assertAttendeeCertificateEligible(attendee, {
      courseName: session?.courseName,
      processNumber: attendee.safePassProcessNumber || undefined,
      cardStatus: attendee.safePassCardStatus,
    });
  }
  // Prefer human TT##### session ref from the session index — never stamp a UUID.
  const refreshedSessionRef =
    (sessionId && ctx.sessionRefById.get(sessionId)) ||
    (sessionId ? sessionReferenceCode(sessionId) : '') ||
    String(row.sessionReference || '').trim();
  const refreshedCourseLanguage =
    (session?.language && String(session.language).trim()) ||
    String(row.courseLanguage || '').trim() ||
    null;

  const originalId = row.id;
  const processed = await resolveProcessedCertificate(row, ctx);
  // Refresh expiry with shared −1 day rule so face matches CRM after regenerate.
  const dateForExpiry =
    processed.courseDates?.length
      ? processed.courseDates[processed.courseDates.length - 1]
      : processed.courseDate;
  const refreshedExpiry = calculateExpiryDate(dateForExpiry, processed.courseName);
  if (refreshedExpiry) {
    processed.expiryDate = refreshedExpiry;
  }

  const blob = await generateCertificatePDF(processed);
  const allocatedId = processed.id;
  const expiryIso =
    refreshedExpiry ||
    (processed as ProcessedCertificate & { __computedExpiryDateISO?: string } & Record<string, unknown>)
      .__computedExpiryDateISO ||
    row.expiryDate ||
    '';

  const customer = await supabaseCustomerStorage.getCustomerById(row.customerId);
  if (customer) {
    const certs = [...(customer.generatedCertificates || [])];
    const resolvedIdx = (() => {
      const matched = findMatchingGeneratedCertificateIndex(certs, {
        currentId: originalId,
        sessionId,
        attendeeId: attendeeId || processed.attendeeId,
        attendeeName: processed.attendeeName,
        courseName: processed.courseName,
        courseDate: processed.courseDate,
        customerId: row.customerId,
      });
      if (matched >= 0) return matched;
      return certs.findIndex((c) => c.id === allocatedId || c.id === originalId);
    })();
    const base: GeneratedCertificate =
      resolvedIdx >= 0
        ? { ...certs[resolvedIdx] }
        : {
            id: allocatedId,
            attendeeName: processed.attendeeName,
            courseName: processed.courseName,
            courseDate: processed.courseDate,
            expiryDate: expiryIso,
            fileName: processed.fileName,
            generatedAt: new Date().toISOString(),
            status: 'active',
          };

    const updated: GeneratedCertificate = {
      ...base,
      id: isAllocatedCertificateNumber(allocatedId) ? allocatedId : base.id,
      attendeeName: processed.attendeeName,
      courseName: processed.courseName,
      courseDate: processed.courseDate,
      courseDates: processed.courseDates,
      expiryDate: expiryIso || base.expiryDate,
      fileName: processed.fileName,
      instructorName: processed.instructorName,
      truckType: processed.truckType,
      truckMake: processed.truckMake,
      truckCapacity: processed.truckCapacity,
      truckAttachments: processed.truckAttachments,
      instructorRegNumber: processed.instructorRegNumber,
      status: base.status || 'active',
      generatedAt: base.generatedAt || new Date().toISOString(),
      sessionId: base.sessionId || sessionId || row.sessionId,
      attendeeId: base.attendeeId || attendeeId || processed.attendeeId,
      sessionReference: refreshedSessionRef || base.sessionReference || null,
      courseLanguage: refreshedCourseLanguage || base.courseLanguage || null,
    };

    if (resolvedIdx >= 0) certs[resolvedIdx] = updated;
    else certs.push(updated);

    await supabaseCustomerStorage.updateCustomer({
      ...customer,
      generatedCertificates: certs,
      numCertsGenerated: certs.length,
    });

    try {
      await upsertIssuedCertificateFromGenerated({
        cert: updated,
        customerId: row.customerId,
        issuedBy: customer.companyName || updated.attendeeName,
      });
    } catch (e) {
      console.error('issued_certificates upsert on regenerate failed', e);
    }
  }

  return { blob, processed };
}